import React, { useState, useRef } from 'react';
import { Play, Pause, Monitor, Smartphone, Truck, BarChart3, Package, Users } from 'lucide-react';
import { COLORS } from './landingData';

const videoData = {
  en: {
    overline: 'SEE IT IN ACTION',
    title: 'Watch How PharmX Works',
    subtitle: 'Quick video walkthroughs of our core features — see the platform in action before you sign up.',
    videos: [
      {
        id: 'dashboard',
        icon: Monitor,
        title: 'Dashboard & Analytics',
        desc: 'Real-time KPIs, cash flow tracking, and reliability scores at a glance.',
        color: '#2E8B6E',
        duration: '2:30',
      },
      {
        id: 'mobile',
        icon: Smartphone,
        title: 'MR Mobile App',
        desc: 'Field rep mobile dashboard — works offline, GPS check-ins, payment collection.',
        color: '#3BA08A',
        duration: '3:15',
      },
      {
        id: 'delivery',
        icon: Truck,
        title: 'Delivery Management',
        desc: 'Create deliveries with automatic stock deduction, tracking, and exposure control.',
        color: '#1a3a28',
        duration: '2:45',
      },
      {
        id: 'catalog',
        icon: Package,
        title: 'Product Catalog',
        desc: 'Regional pricing, bulk upload from Excel, and real-time inventory sync.',
        color: '#2d6b48',
        duration: '1:50',
      },
      {
        id: 'analytics',
        icon: BarChart3,
        title: 'Business Intelligence',
        desc: 'Growth analytics, district performance, and inventory risk alerts.',
        color: '#247a5e',
        duration: '2:10',
      },
      {
        id: 'team',
        icon: Users,
        title: 'Team & MR Management',
        desc: 'Assign territories, track visits, set targets, and monitor field performance.',
        color: '#2E8B6E',
        duration: '2:00',
      },
    ],
  },
  ru: {
    overline: 'СМОТРИТЕ В ДЕЙСТВИИ',
    title: 'Как работает PharmX',
    subtitle: 'Короткие видео-обзоры основных функций — увидьте платформу до регистрации.',
    videos: [
      { id: 'dashboard', icon: Monitor, title: 'Панель управления', desc: 'KPI в реальном времени, отслеживание денежных потоков и рейтинги надёжности.', color: '#2E8B6E', duration: '2:30' },
      { id: 'mobile', icon: Smartphone, title: 'Мобильное приложение МП', desc: 'Мобильная панель для полевых представителей — работает офлайн, GPS-отметки.', color: '#3BA08A', duration: '3:15' },
      { id: 'delivery', icon: Truck, title: 'Управление доставками', desc: 'Создание доставок с автоматическим списанием склада и отслеживанием.', color: '#1a3a28', duration: '2:45' },
      { id: 'catalog', icon: Package, title: 'Каталог продуктов', desc: 'Региональные цены, массовая загрузка из Excel и синхронизация запасов.', color: '#2d6b48', duration: '1:50' },
      { id: 'analytics', icon: BarChart3, title: 'Бизнес-аналитика', desc: 'Аналитика роста, показатели по районам и оповещения о рисках.', color: '#247a5e', duration: '2:10' },
      { id: 'team', icon: Users, title: 'Управление командой', desc: 'Назначение территорий, отслеживание визитов, установка целей.', color: '#2E8B6E', duration: '2:00' },
    ],
  },
  tj: {
    overline: 'ДАР АМАЛ БИНЕД',
    title: 'PharmX чӣ гуна кор мекунад',
    subtitle: 'Видеоҳои кӯтоҳ дар бораи имконотҳои асосӣ — платформаро пеш аз сабтном бинед.',
    videos: [
      { id: 'dashboard', icon: Monitor, title: 'Панели идоракунӣ', desc: 'KPI дар вақти воқеӣ, пайгирии пулгузаронӣ ва рейтинги эътимод.', color: '#2E8B6E', duration: '2:30' },
      { id: 'mobile', icon: Smartphone, title: 'Барномаи мобилӣ', desc: 'Панели мобилии намояндагони саҳроӣ — офлайн кор мекунад.', color: '#3BA08A', duration: '3:15' },
      { id: 'delivery', icon: Truck, title: 'Идоракунии расонидан', desc: 'Сохтани расонидан бо кам кардани худкори анбор.', color: '#1a3a28', duration: '2:45' },
      { id: 'catalog', icon: Package, title: 'Каталоги молҳо', desc: 'Нархҳои минтақавӣ, боркунии оммавӣ аз Excel.', color: '#2d6b48', duration: '1:50' },
      { id: 'analytics', icon: BarChart3, title: 'Таҳлили бизнес', desc: 'Таҳлили рушд ва огоҳиҳо дар бораи хатарҳо.', color: '#247a5e', duration: '2:10' },
      { id: 'team', icon: Users, title: 'Идоракунии гурӯҳ', desc: 'Таъйини минтақаҳо, пайгирии боздидҳо ва мониторинг.', color: '#2E8B6E', duration: '2:00' },
    ],
  },
};

export function VideoSection({ lang }) {
  const t = videoData[lang] || videoData.en;
  const [activeVideo, setActiveVideo] = useState(null);
  const videoRefs = useRef({});

  const handleCardClick = (videoId) => {
    if (activeVideo === videoId) {
      setActiveVideo(null);
    } else {
      setActiveVideo(videoId);
    }
  };

  return (
    <section id="videos" style={{ background: COLORS.bg, padding: '80px 24px' }} data-testid="video-section">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={{
            fontFamily: 'Outfit, sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.15em',
            color: COLORS.accent, textTransform: 'uppercase',
          }}>
            {t.overline}
          </span>
          <h2 style={{
            fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 38px)', fontWeight: 800,
            color: COLORS.text, marginTop: 8, lineHeight: 1.2, marginBottom: 12,
          }}>
            {t.title}
          </h2>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
            {t.subtitle}
          </p>
        </div>

        {/* Video Grid */}
        <div className="video-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 20,
        }}>
          {t.videos.map((video) => {
            const Icon = video.icon;
            const isActive = activeVideo === video.id;
            const videoSrc = `/videos/${video.id}.mp4`;
            return (
              <div
                key={video.id}
                data-testid={`video-card-${video.id}`}
                onClick={() => handleCardClick(video.id)}
                style={{
                  background: COLORS.surface,
                  borderRadius: 18,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: isActive
                    ? `0 12px 40px rgba(46,139,110,0.2), 0 0 0 2px ${COLORS.secondary}`
                    : '0 2px 12px rgba(0,0,0,0.06)',
                  transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
                }}
              >
                {/* Video Preview */}
                <div style={{
                  position: 'relative',
                  height: 180,
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${video.color}18 0%, ${video.color}08 100%)`,
                }}>
                  <video
                    muted
                    loop
                    playsInline
                    autoPlay
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      display: 'block',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  >
                    <source src={videoSrc} type="video/mp4" />
                  </video>

                  {/* Play overlay */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isActive ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.25)',
                    transition: 'background 0.3s ease',
                  }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.95)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                      transition: 'transform 0.3s ease',
                      transform: isActive ? 'scale(1.15)' : 'scale(1)',
                    }}>
                      <Play size={20} color={video.color} fill={video.color} style={{ marginLeft: 2 }} />
                    </div>
                  </div>

                  {/* Duration badge */}
                  <div style={{
                    position: 'absolute', bottom: 10, right: 10,
                    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
                    padding: '3px 8px', borderRadius: 5,
                    fontSize: 10, fontWeight: 700, color: '#fff',
                    fontFamily: 'Outfit, sans-serif',
                  }}>
                    {video.duration}
                  </div>
                </div>

                {/* Info */}
                <div style={{ padding: '16px 18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 7,
                      background: `${video.color}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={15} color={video.color} />
                    </div>
                    <h3 style={{
                      fontFamily: 'Outfit, sans-serif', fontSize: 15, fontWeight: 700,
                      color: COLORS.text, margin: 0,
                    }}>
                      {video.title}
                    </h3>
                  </div>
                  <p style={{
                    fontFamily: 'Outfit, sans-serif', fontSize: 12.5, color: COLORS.textSecondary,
                    margin: 0, lineHeight: 1.5,
                  }}>
                    {video.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expanded Video Player */}
        {activeVideo && (
          <div style={{
            marginTop: 32,
            background: '#0e1e2f',
            borderRadius: 22,
            padding: 6,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} data-testid="video-player-expanded">
            <video
              ref={el => { videoRefs.current[activeVideo] = el; }}
              key={activeVideo}
              controls
              autoPlay
              playsInline
              style={{
                width: '100%',
                aspectRatio: '16/9',
                borderRadius: 18,
                background: '#1a2d3d',
                display: 'block',
                objectFit: 'cover',
              }}
            >
              <source src={`/videos/${activeVideo}.mp4`} type="video/mp4" />
            </video>
            <div style={{
              padding: '12px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{
                fontFamily: 'Outfit, sans-serif', fontSize: 14, fontWeight: 700,
                color: 'rgba(255,255,255,0.9)',
              }}>
                {(t.videos.find(v => v.id === activeVideo) || {}).title}
              </span>
              <span style={{
                fontFamily: 'Outfit, sans-serif', fontSize: 12,
                color: 'rgba(255,255,255,0.5)',
              }}>
                {(t.videos.find(v => v.id === activeVideo) || {}).desc}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 900px) {
          .video-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .video-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
